import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import MenuItem from '@mui/material/MenuItem';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import { Tooltip } from '@katalon-studio/katalon-ui/v2';
import React, { useImperativeHandle } from 'react';
import { Account } from '../../../models';
import { useCheckPermission } from '../../../hook/useCheckPermission';

const useStyles = makeStyles(theme => ({
  accountInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignContent: 'center',
    justifyContent: 'flex-start',
    border: `1px solid ${theme.common.grey[700]}`,
    margin: theme.spacing(2, 0),
    padding: theme.spacing(2, 2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.borderRadius.radiusRounded8px,
    '&:hover': {
      border: `1px solid ${theme.refV2.cat.purple[500]}`,
      backgroundColor: theme.palette.action.hover,
    },
  },
  selected: {
    border: `1px solid ${theme.refV2.cat.purple[500]}`,
    backgroundColor: theme.refV2.cat.purple_a[800],
    '&:hover': {
      border: `1px solid ${theme.refV2.cat.purple[500]}`,
      backgroundColor: theme.refV2.cat.purple_a[800],
    },
  },
}));

const MAX_ACCOUNT_NAME_LENGTH = 40;

export const AccountInfo = React.forwardRef((
  {
    account,
    selected,
    onSelectAccount,
  } : {
    account: Account,
    selected: boolean,
    onSelectAccount: (account: Account) => void,
  },
  ref,
) => {
  const classes = useStyles();
  const { id, name, domain } = account;
  const key = `account-selection-account-info-${id}`;
  const accountDomain = domain ?? `acc-${id}`;
  const testOpsUrl = process.env.REACT_APP_TESTOPS_G3_URL?.replace('{accountDomain}', accountDomain);
  const { allowed, loading } = useCheckPermission(account);
  const handleSelectAccount = () => {
    onSelectAccount(account);
  };

  useImperativeHandle(ref, () => ({ loading, allowed }));

  const renderAccountName = () => (
    <Box width="100%">
      <Typography
        variant="h4"
        fontWeight={600}
        color={theme => theme.palette.text.primary}
        overflow="hidden"
        textOverflow="ellipsis"
      >
        <FormattedMessage
          id="page.account.selection.account.info.label"
          values={{
            accountId: id,
            accountName: name,
          }}
        />
        {selected}
      </Typography>
      {account.g3
        ? testOpsUrl && (
        <Typography
          variant="subtitle1"
          fontWeight={400}
          color={theme => theme.palette.text.secondary}
        >
          {testOpsUrl}
        </Typography>
        )
        : (
          <Typography
            variant="subtitle1"
            fontWeight={400}
            color={theme => theme.palette.text.secondary}
          >
            {account.name}
          </Typography>
        )}
    </Box>
  );

  const renderItem = () => (
    <MenuItem
      key={key}
      onClick={handleSelectAccount}
      className={clsx(
        classes.accountInfo,
        { [classes.selected]: selected },
      )}
    >
      {account.name.length > MAX_ACCOUNT_NAME_LENGTH
        ? (
          <Tooltip
            key={`${key}-tooltip`}
            title={account.name}
            placement="top-start"
          >
            {renderAccountName()}
          </Tooltip>
        )
        : renderAccountName()}
    </MenuItem>
  );

  return allowed ? renderItem() : null;
});
